import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { saveAs } from 'file-saver';
import moment from 'moment';
import '../../styles/assets.css'
import '../../styles/travelrequest.css'; // optional external CSS
import { useUser } from '../../utils/userContext';
import useHandleErrors from '../../utils/user.utils';
import { toast, ToastContainer } from 'react-toastify';
import { utils, write } from 'xlsx';
import Select from 'react-select';
import jsPDF from 'jspdf';
import wwImg from '../../assets/globalImgs/WW-360-Logo.png'
import useCountryCodes from "../../utils/useCountryCodes";

const RequestStatus = () => {
    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [modalData, setModalData] = useState(null);
    const [updateModalData, setUpdateModalData] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [recordsPerPage, setRecordsPerPage] = useState(10);
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [fileData, setFileData] = useState([]);
    const [file, setFile] = useState(null);
    const { user } = useUser();
    const handleErrors = useHandleErrors();
    const [searchQuery, setSearchQuery] = useState('');
    const token = localStorage.getItem("token");
    const [loading, setLoading] = useState(false);
    const hasFetchedRef = useRef(false);
    const [employees, setEmployees] = useState([]);
    const [updateData, setUpdateData] = useState({
        AssignTo: '',
        Comments: '',
        Document: null,
    });
    // Journey type filter state
    const [journeyTypeFilter, setJourneyTypeFilter] = useState('');
    // Get unique journey types from data
    const journeyTypes = Array.from(new Set(data.map(item => item.typeOfTravel)));

    const fetchTravelDeskDetails = async () => {
        if (user && user?.EmployeeId) {
            const path = `/gettraveldeskDetails`;
            const URL = process.env.REACT_APP_BE_URL + path;
            try {
                const response = await axios.get(URL, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                if (response.status === 200) {
                    setData(response.data.travelDeskDetails.filter(item => item.status.toLowerCase() === 'approved' || item.status.toLowerCase() === 'booking in progress' || item.status.toLowerCase() === 'booked'));
                    setFilteredData(response.data.travelDeskDetails.filter(item => item.status.toLowerCase() === 'approved' || item.status.toLowerCase() === 'booking in progress' || item.status.toLowerCase() === 'booked')); // Assuming you want to filter the merged data
                } else {
                    handleErrors(response);
                }
            } catch (error) {
                if (error.response) {
                    if (error.response.status === 404) {
                        setData([]); // Set data to an empty array
                    } else {
                        handleErrors(error.response);
                    }
                } else {
                    toast.error("Something went wrong. Please try again later.", {
                        position: toast.POSITION.TOP_CENTER,
                    });
                }
            }
        }
    };

    const fetchTravelPartnerDetails = async () => {
        if (user && user?.EmployeeId) {
            const path = `/getpartnervendoronDetails`;
            const URL = process.env.REACT_APP_BE_URL + path;
            try {
                const response = await axios.get(URL, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                if (response.status === 200) {
                    const activeEmployees = response.data.partnerVendorDetails;
                    setEmployees(activeEmployees);
                } else {
                    handleErrors(response);
                }
            } catch (error) {
                if (error.response) {
                    if (error.response.status === 404) {
                        setData([]); // Set data to an empty array
                    } else {
                        handleErrors(error.response);
                    }
                } else {
                    toast.error("Something went wrong. Please try again later.", {
                        position: toast.POSITION.TOP_CENTER,
                    });
                }
            }
        }
    };

    useEffect(() => {
        if (!hasFetchedRef.current) {
            fetchTravelDeskDetails();
            fetchTravelPartnerDetails();
            hasFetchedRef.current = true;
        }
    }, [user, fetchTravelDeskDetails, fetchTravelPartnerDetails]);

    useEffect(() => {
        // Filter employees based on the search query and journey type
        setFilteredData(
            data?.filter(data =>
                (journeyTypeFilter ? data.typeOfTravel === journeyTypeFilter : true) &&
                (
                    data.travelId.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    data.typeOfTravel.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    data.approvedBy.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    data.status.toLowerCase().includes(searchQuery.toLowerCase())
                )
            )
        );
    }, [searchQuery, data, journeyTypeFilter]);

    const sendEmail = (email) => {
        // Excel generation logic
        const ws = utils.json_to_sheet(filteredData);
        const wb = utils.book_new();
        utils.book_append_sheet(wb, ws, 'TravelStatus');
        const excelBuffer = write(wb, { bookType: 'xlsx', type: 'array' });

        // Create a downloadable link for the attachment
        const data = new Blob([excelBuffer], { type: 'application/octet-stream' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(data);
        const today = new Date();
        const year = today.getFullYear().toString().slice(-2); // Get last two digits of the year
        const month = (today.getMonth() + 1).toString().padStart(2, '0'); // Add leading 0 for month
        const day = today.getDate().toString().padStart(2, '0'); // Add leading 0 for day
        const formattedDate = `${year}${month}${day}`;
        link.download = `WW360-Travel_Status_Report_${formattedDate}.xlsx`;

        // Simulate a click to download the attachment
        link.click();

        // Prepare the mailto link (without attachment)
        const subject = "Assigned Asset List";
        const body = 'Please find the attached file.';
        const mailtoLink = `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

        toast.info('The Excel file has been generated and downloaded. Please attach the file manually to your email.', {
            position: toast.POSITION.TOP_CENTER,
        });

        setTimeout(() => {
            window.location.href = mailtoLink;
        }, 3000);
    };

    const handleExport = () => {
        // Only include specific fields in the Excel export
        const selectedFields = filteredData.map(item => ({
            TravelID: item.travelId,
            EmployeeID: item.employeeId,
            EmployeeName: item.employeeName,
            TravelType: item.typeOfTravel,
            TravelFrom: item.travelFrom,
            TravelTo: item.travelTo,
            TravelStartDate: item.travelStartDate,
            TravelEndDate: item.travelEndDate,
            TripType: item.tripType,
            Approval: item.approvedBy,
            AssignTo: item.assignTo,
            Agency: item.travelAgency,
            Documents: item.travelDocuments
                ? item.travelDocuments.map(url =>
                    url.split('?')[0].split('/').pop()
                ).join(', ')
                : '',
            Status: item.status,
        }));
        const ws = utils.json_to_sheet(selectedFields);
        const wb = utils.book_new();
        utils.book_append_sheet(wb, ws, 'Assets');
        const excelBuffer = write(wb, { bookType: 'xlsx', type: 'array' });
        const data = new Blob([excelBuffer], { type: 'application/octet-stream' });
        const today = new Date();
        const year = today.getFullYear().toString().slice(-2); // Get last two digits of the year
        const month = (today.getMonth() + 1).toString().padStart(2, '0'); // Add leading 0 for month
        const day = today.getDate().toString().padStart(2, '0'); // Add leading 0 for day
        const formattedDate = `${year}${month}${day}`;
        saveAs(data, `WW360-Travel_Status_Report_${formattedDate}.xlsx`);
    };

    const shareData = {
        title: 'Travel List',
        text: 'Check out the Travel list',
        url: window.location.href
    };

    const handleShare = async () => {
        try {
            await navigator.share(shareData);
        } catch (err) {
            console.error('Error sharing:', err);
        }
    };

    useEffect(() => {
        const preloading = document.getElementById("preloading");
        setTimeout(() => {
            if (preloading) {
                preloading.style.display = "none";
            }
        }, 400);
    }, []);

    const handleUpdateChange = (field) => (event) => {
        setUpdateData({ ...updateData, [field]: event.target.value });
    };

    const handleFileChange = (e) => {
        setUpdateData({ ...updateData, Document: e.target.files[0] });
        setFile(e.target.files[0]);
    };

    const handleDelete = (fileIndex) => {
        setUploadedFiles((prevFiles) => prevFiles.filter((_, index) => index !== fileIndex));
        setFileData((prevData) => prevData.filter((_, index) => index !== fileIndex));
    };

    const handleUpload = () => {
        if (file) {
            // Store the original file directly instead of reading as binary string
            setUploadedFiles((prevFiles) => [...prevFiles, file]);
            setFileData((prevData) => [...prevData, file]); // Store the actual file object
            setFile(null);
        }
    };

    const statusOptions = [
        { value: 'Booked', label: 'Booked' },
        { value: 'Cancelled', label: 'Cancelled' },
    ];

    const openUpdateModel = (item) => {
        setUpdateModalData(item);
    };

    const openModal = (item) => {
        setModalData(item);
    };

    const closeModal = () => {
        setModalData(null);
        setUpdateModalData(null);
        setUpdateData({ Status: "", AssignTo: "", Comments: "", Document: "" });
        setFileData([]);
        setUploadedFiles([]);
    };

    const assignToOptions = employees
        .filter(emp => emp.UserType.toLowerCase() === 'partner')
        .map((emp) => ({
            value: emp.Email,
            label: `${emp.Email}`,
        }));
    console.log("assignToOptions", assignToOptions);
    const ClearIndicator = () => null;

    const handleSubmit = async () => {

        const formData = new FormData();
        setLoading(true);

        // Use the status from GET API if not selected
        const statusToSend = updateData.status || updateModalData?.status || '';
        const travelAgencyToSend = updateData.travelAgency || updateModalData?.travelAgency || '';

        if (!statusToSend) {
            toast.error("Status is required.", {
                position: toast.POSITION.TOP_CENTER,
            });
            setLoading(false);
            return;
        }
        if (!travelAgencyToSend && statusToSend === 'Booked') {
            toast.error("Travel Agency to is required.", {
                position: toast.POSITION.TOP_CENTER,
            });
            setLoading(false);
            return;
        }

        formData.append('EmployeeId', updateModalData.employeeId);
        formData.append('TravelId', updateModalData.travelId);
        formData.append('Status', statusToSend);
        if (updateData.travelAgency) {
            formData.append('TravelAgency', updateData.travelAgency);
        }
        formData.append('AssignTo', updateModalData.assignTo);
        formData.append('comments', updateData.Comments);
        formData.append('UpdatedBy', user?.EmployeeId);
        uploadedFiles.forEach((file, index) => {
            formData.append('travelDocument', new Blob([fileData[index]], { type: file.type }), file.name);
        });

        try {
            const path = "/travelDeskupdate";
            const URL = process.env.REACT_APP_BE_URL + path;
            const response = await axios.put(URL, formData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data',
                },
            });
            if (response.status === 200) {
                setModalData(null);
                toast.success("Request Updated successfully!", {
                    position: toast.POSITION.TOP_CENTER,
                });
                setUpdateData({ Status: "", AssignTo: "", Comments: "", Document: "" });
                setUploadedFiles([]);
                fetchTravelDeskDetails();
            } else {
                handleErrors(response);
            }
        } catch (error) {
            if (error.response) {
                handleErrors(error.response);
            } else {
                toast.error("Something went wrong. Please try again later.", {
                    position: toast.POSITION.TOP_CENTER,
                });
            }
        } finally {
            setLoading(false);
        }
    };

    const { countryCodes } = useCountryCodes();

    // pagination
    const handleRecordsPerPageChange = (event) => {
        setRecordsPerPage(Number(event.target.value));
        setCurrentPage(1);
    };
    const indexOfLastRecord = currentPage * recordsPerPage;
    const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
    const currentRecords = filteredData?.slice(indexOfFirstRecord, indexOfLastRecord);
    const totalPages = Math.ceil(filteredData?.length / recordsPerPage);
    const paginate = pageNumber => setCurrentPage(pageNumber);
    // pagination end

    const handleDownloadPDF = (record) => {
        const doc = new jsPDF();

        // Define page margins and dimensions
        const marginLeft = 10;
        const marginRight = 10;
        const pageWidth = doc.internal.pageSize.width;

        // Header
        // Add logo on the left side
        const imgData = wwImg; // Replace with your actual logo path or base64 string
        doc.addImage(imgData, 'PNG', marginLeft, 10, 40, 15); // Logo positioned on the left

        // Add timestamp on the right side in small size
        const timestamp = new Date().toLocaleString();
        doc.setFontSize(8);
        doc.text(timestamp, pageWidth - marginRight, 15, { align: 'right' });

        // Add heading
        doc.setFontSize(12);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(37, 183, 64); // Set text color to #25b740 (RGB: 37, 183, 64)
        doc.text("Travel Request", pageWidth / 2, 30, { align: 'center' });

        // Reset text color to black for other text
        doc.setTextColor(0, 0, 0);

        // Add employee details
        doc.setFontSize(10);
        doc.setFont("helvetica", "Bold");
        doc.setTextColor(0, 79, 124); // Set text color to #004f7c (RGB: 0, 79, 124)
        const employeeDetailsStartY = 45; // Start Y position for employee details
        doc.text("Employee Details", marginLeft, employeeDetailsStartY);
        doc.setTextColor(0, 0, 0);
        // Employee details
        const employeeDetails = [
            { label: 'Employee ID', value: record.employeeId },
        ];

        // Draw employee details
        employeeDetails.forEach((detail, index) => {
            const y = employeeDetailsStartY + 10 + (index * 10);
            doc.text(`${detail.label}: ${detail.value}`, marginLeft, y);
        });

        // Draw a thin line above the table
        const lineY = employeeDetailsStartY + 10 + (employeeDetails.length * 10) + 5;
        doc.setDrawColor(0, 0, 0);
        // doc.setLineWidth(0.5); // Standard line width
        doc.line(marginLeft, lineY, pageWidth - marginRight, lineY);

        // Add "Details" heading
        const detailsHeadingY = lineY + 10; // Y position for "Details" heading
        doc.setFontSize(12);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(0, 79, 124); // Set text color to #004f7c (RGB: 0, 79, 124)
        doc.text("Travel Request Details", marginLeft, detailsHeadingY);

        // Define table headers
        const headers = ['Field', 'Value'];
        doc.setTextColor(0, 0, 0);
        const rows = [
            { key: 'Travel Id', value: `${record.travelId}` },
            { key: 'Employee Id', value: `${record.employeeId}` },
            { key: 'Employee Name', value: `${record.employeeName}` },
            { key: 'Travel Type', value: `${record.typeOfTravel}` },
            { key: 'From', value: `${record.travelFrom}` },
            { key: 'To', value: `${record.travelTo}` },
            { key: 'Travel Date', value: moment(`${record.travelStartDate}`).format("DD-MM-YYYY") },
            { key: 'Assign To', value: `${record.assignTo}` },
            { key: 'Status', value: `${record.status}` },
        ];

        // Define table positions and dimensions
        const tableStartY = detailsHeadingY + 20; // Add space below "Details" heading
        const rowHeight = 10;
        const columnWidth = {
            field: 80,
            value: pageWidth - 100 // Adjust as needed to fit the page width
        };

        // Draw table headers (Field and Value)
        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.setDrawColor(0, 0, 0);
        // doc.setLineWidth(0.5); // Normal line width

        // Draw header cell borders
        doc.rect(marginLeft, tableStartY - rowHeight, columnWidth.field, rowHeight); // Field header cell border
        doc.rect(marginLeft + columnWidth.field, tableStartY - rowHeight, columnWidth.value, rowHeight); // Value header cell border

        // Add header text
        doc.text(headers[0], marginLeft + 5, tableStartY - rowHeight / 2 + 3); // "Field" header text
        doc.text(headers[1], marginLeft + columnWidth.field + 5, tableStartY - rowHeight / 2 + 3); // "Value" header text

        // Reset font to normal for table data rows
        doc.setFont("helvetica", "normal");

        // Draw table rows
        rows.forEach((row, index) => {
            const y = tableStartY + (index * rowHeight) + rowHeight / 2; // Adjusted to center the text vertically
            doc.text(row.key, marginLeft + 5, y); // Field value
            doc.text(row.value, marginLeft + columnWidth.field + 5, y); // Value (left-aligned)

            // Draw row borders
            doc.setDrawColor(0, 0, 0);
            doc.rect(marginLeft, y - rowHeight / 2, columnWidth.field, rowHeight); // Row border for Field
            doc.rect(marginLeft + columnWidth.field, y - rowHeight / 2, columnWidth.value, rowHeight); // Row border for Value
        });

        // Add "Approved By" and "Name" text
        const approvedByY = tableStartY + (rows.length * rowHeight) + 20; // Position below the table
        const nameText = `${record.approvedBy}`;
        const approvedByText = "Approved By";

        // Add the text aligned to the right side
        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");

        // "Name" text
        doc.text(nameText, pageWidth - marginRight - 50, approvedByY); // Adjust -50 to move it closer or further from the right

        // "Approved By" text below "Name"
        doc.text(approvedByText, pageWidth - marginRight - 50, approvedByY + 10); // 10 units below "Name"

        // Add footer with timestamp and additional data, aligned left and right
        doc.setFontSize(10);
        const footerTimestamp = `Timestamp: ${timestamp}`;
        const footerText = "Generated By WorkWise360";
        doc.text(footerTimestamp, marginLeft, 280); // Left-aligned footer
        doc.text(footerText, pageWidth - marginRight, 280, { align: 'right' }); // Right-aligned footer

        // Save the PDF
        doc.save(`Travel_Request_${record.travelId}.pdf`);
    };

    return (
        <div>
            {loading && (
                <div id="preloading">
                    <span className="loader"></span>
                </div>
            )}
            <ToastContainer />
            <div className="content-page">
                <div className="container-fluid">
                    <div className="row mb-2">
                        <div className="col">
                            <h3 className="travel-icons looped">
                                <span className="icon bus">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="white" viewBox="0 0 256 256"><path d="M184,32H72A32,32,0,0,0,40,64V208a16,16,0,0,0,16,16H80a16,16,0,0,0,16-16V192h64v16a16,16,0,0,0,16,16h24a16,16,0,0,0,16-16V64A32,32,0,0,0,184,32ZM56,176V120H200v56Zm0-96H200v24H56ZM72,48H184a16,16,0,0,1,16,16H56A16,16,0,0,1,72,48Zm8,160H56V192H80Zm96,0V192h24v16Zm-72-60a12,12,0,1,1-12-12A12,12,0,0,1,104,148Zm72,0a12,12,0,1,1-12-12A12,12,0,0,1,176,148Zm72-68v24a8,8,0,0,1-16,0V80a8,8,0,0,1,16,0ZM24,80v24a8,8,0,0,1-16,0V80a8,8,0,0,1,16,0Z"></path></svg>
                                </span>
                                <span className="icon train">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="white" viewBox="0 0 256 256"><path d="M184,24H72A32,32,0,0,0,40,56V184a32,32,0,0,0,32,32h8L65.6,235.2a8,8,0,1,0,12.8,9.6L100,216h56l21.6,28.8a8,8,0,1,0,12.8-9.6L176,216h8a32,32,0,0,0,32-32V56A32,32,0,0,0,184,24ZM72,40H184a16,16,0,0,1,16,16v64H56V56A16,16,0,0,1,72,40ZM184,200H72a16,16,0,0,1-16-16V136H200v48A16,16,0,0,1,184,200ZM96,172a12,12,0,1,1-12-12A12,12,0,0,1,96,172Zm88,0a12,12,0,1,1-12-12A12,12,0,0,1,184,172Z"></path></svg>
                                </span>
                                <span className="icon flight">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="white" viewBox="0 0 256 256"><path d="M185.33,114.21l29.14-27.42.17-.17a32,32,0,0,0-45.26-45.26c0,.06-.11.11-.17.17L141.79,70.67l-83-30.2a8,8,0,0,0-8.39,1.86l-24,24a8,8,0,0,0,1.22,12.31l63.89,42.59L76.69,136H56a8,8,0,0,0-5.65,2.34l-24,24A8,8,0,0,0,29,175.42l36.82,14.73,14.7,36.75.06.16a8,8,0,0,0,13.18,2.47l23.87-23.88A8,8,0,0,0,120,200V179.31l14.76-14.76,42.59,63.89a8,8,0,0,0,12.31,1.22l24-24a8,8,0,0,0,1.86-8.39Zm-.07,97.23-42.59-63.88A8,8,0,0,0,136.8,144c-.27,0-.53,0-.79,0a8,8,0,0,0-5.66,2.35l-24,24A8,8,0,0,0,104,176v20.69L90.93,209.76,79.43,181A8,8,0,0,0,75,176.57l-28.74-11.5L59.32,152H80a8,8,0,0,0,5.66-2.34l24-24a8,8,0,0,0-1.22-12.32L44.56,70.74l13.5-13.49,83.22,30.26a8,8,0,0,0,8.56-2L180.78,52.6A16,16,0,0,1,203.4,75.23l-32.87,30.93a8,8,0,0,0-2,8.56l30.26,83.22Z"></path></svg>
                                </span>
                                <span className="icon hotel">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="white" viewBox="0 0 256 256"><path d="M248,208H232V96a8,8,0,0,0,0-16H184V48a8,8,0,0,0,0-16H40a8,8,0,0,0,0,16V208H24a8,8,0,0,0,0,16H248a8,8,0,0,0,0-16ZM216,96V208H184V96ZM56,48H168V208H144V160a8,8,0,0,0-8-8H88a8,8,0,0,0-8,8v48H56Zm72,160H96V168h32ZM72,80a8,8,0,0,1,8-8H96a8,8,0,0,1,0,16H80A8,8,0,0,1,72,80Zm48,0a8,8,0,0,1,8-8h16a8,8,0,0,1,0,16H128A8,8,0,0,1,120,80ZM72,120a8,8,0,0,1,8-8H96a8,8,0,0,1,0,16H80A8,8,0,0,1,72,120Zm48,0a8,8,0,0,1,8-8h16a8,8,0,0,1,0,16H128A8,8,0,0,1,120,120Z"></path></svg>
                                </span>
                            </h3>
                        </div>
                    </div>
                    {data?.length === 0 ? (
                        <div className="container">
                            <div className="row vh-50 justify-content-center align-items-center">
                                <div className="col-auto">
                                    <div className="no-data-wrapper">
                                        <div className="no-data-found">
                                            <i className="ri-file-damage-fill"></i>
                                            <div className="bounce"><i className="ri-search-eye-line"></i></div>
                                            <p>No Data Found</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div>
                            <div className="row align-items-center justify-content-center">
                                <div className="col-5">
                                    <div className="input-group mb-0">
                                        <input
                                            type="search"
                                            className="form-control search-input"
                                            placeholder="Keyword search..."
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                        />
                                        <div className="input-group-append">
                                            <button className="btn btn-primary" type="button" title="Search" rel="tooltip"><i className="ri-menu-search-line"></i></button>
                                        </div>
                                    </div>
                                    <small className="form-text text-muted text-center mt-0">
                                        <i className="ri-information-2-line"></i> Allows Booking ID-Journey Type -Current Status
                                    </small>
                                </div>
                            </div>
                            <br></br>
                            <div className="row align-items-center justify-content-center">
                                <div className="col-2">
                                    <select
                                        id="journeyTypeFilter"
                                        className="form-control form-control-sm"
                                        placeholder="Select Journey Type"
                                        style={{ fontSize: "12px", padding: "2px 6px", height: "28px" }}
                                        value={journeyTypeFilter}
                                        onChange={e => setJourneyTypeFilter(e.target.value)}
                                    >
                                        <option value="">All Journey Types</option>
                                        {journeyTypes.map((type, idx) => (
                                            <option key={idx} value={type}>{type}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col">
                                    <h5 className="title mt-1 mb-0">Travel Bookings</h5>
                                </div>
                                <div className="col-auto">
                                    <button type="button" title="Email" className="btn btn-light btn-circle ml-2" onClick={() => sendEmail('')}>
                                        <i className="ri-mail-send-fill"></i>
                                    </button>
                                    <button type="button" title="Share" className="btn btn-light btn-circle ml-2" onClick={handleShare}>
                                        <i className="ri-share-fill"></i>
                                    </button>
                                    <button type="button" title="Export" className="btn btn-light btn-circle ml-2" onClick={handleExport}>
                                        <i className="ri-export-fill"></i>
                                    </button>
                                </div>
                            </div>

                            <div className="row mt-3">
                                <div className="col-md-12">
                                    <div className="table-responsive asset-information">
                                        <table className="table grid">
                                            <thead className="bg-light">
                                                <tr>
                                                    {/* <th>S.No.</th> */}
                                                    <th>Booking ID</th>
                                                    <th>Emp ID</th>
                                                    <th>Emp Name</th>
                                                    <th>Journey Type</th>
                                                    <th>{journeyTypeFilter === 'Hotels' ? 'Location' : 'From'}</th>
                                                    {journeyTypeFilter !== 'Hotels' && <th>To</th>}
                                                    <th>Booking Date</th>
                                                    <th>Current Status</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {currentRecords?.map((item, index) => (
                                                    <tr key={index}>
                                                        {/* <td>{index + 1 + indexOfFirstRecord}</td> */}
                                                        <td>{item.travelId}</td>
                                                        <td>{item.employeeId}</td>
                                                        <td>{item.employeeName}</td>
                                                        <td>{item.typeOfTravel}</td>
                                                        <td style={{ maxWidth: '150px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={item.travelFrom}>{item.travelFrom}</td>
                                                        {journeyTypeFilter !== 'Hotels' && (
                                                            <td style={{ maxWidth: '150px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={item.travelTo}>{item.travelTo}</td>
                                                        )}
                                                        <td>{moment(item.travelStartDate).format("DD-MM-YYYY")}</td>
                                                        <td>
                                                            {item.status.trim().toLowerCase() === 'approved' ? (
                                                                <span className="badge border border-warning text-warning status">
                                                                    <i className="ri-hourglass-fill"></i> {'Booking in Progress'}
                                                                </span>
                                                            ) : item.status.trim().toLowerCase() === 'booked' ? (
                                                                <span className="badge border border-success text-success status">
                                                                    <i className="ri-thumb-up-fill"></i> {item.status.trim()}
                                                                </span>
                                                            ) : item.status.trim().toLowerCase() === 'rejected' ? (
                                                                <span className="badge border border-danger text-danger status">
                                                                    <i className="ri-thumb-down-line"></i> {item.status.trim()}
                                                                </span>
                                                            ) : (
                                                                <span className="badge border border-secondary text-secondary status">
                                                                    <i className="ri-thumb-up-fill"></i> {item.status.trim()}
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td className="text-center">
                                                            {(item.status.toLowerCase() === 'approved' && item.assignTo === user?.OfficialEmail) ||
                                                                (item.status.toLowerCase() === 'approved' && user?.UserAccessLevel.toLowerCase() === 'admin') ? (
                                                                <button
                                                                    className="btn btn-link text-info btn-action"
                                                                    type="button"
                                                                    title="List View"
                                                                    onClick={() => openUpdateModel(item)}
                                                                >
                                                                    <i className="ri-list-settings-line"></i>
                                                                </button>
                                                            ) :
                                                                (
                                                                    <>
                                                                        <button
                                                                            className="btn btn-link text-info btn-action"
                                                                            type="button"
                                                                            title="List View"
                                                                            onClick={() => openModal(item)}
                                                                        >
                                                                            <i className="ri-layout-horizontal-line"></i>
                                                                        </button>
                                                                        <button
                                                                            className="btn btn-link text-info btn-action custom-dropdown-btn"
                                                                            type="button"
                                                                            title="Download"
                                                                            rel="tooltip"
                                                                            onClick={() => handleDownloadPDF(item)}
                                                                        >
                                                                            <i className="ri-download-2-line"></i>
                                                                        </button>
                                                                    </>
                                                                )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>

                            <div className="col-md-12">
                                <div className="container-fluid">
                                    <div className="row border-top pt-3">
                                        <div className="col-auto">
                                            <p className="mt-1 mb-0">Records</p>
                                        </div>
                                        <div className="col-1.2 record-drop">
                                            <select className="form-control" value={recordsPerPage} onChange={handleRecordsPerPageChange}>
                                                <option value="5">5</option>
                                                <option value="10">10</option>
                                                <option value="15">15</option>
                                                <option value="20">20</option>
                                                <option value="25">25</option>
                                            </select>
                                        </div>
                                        <div className="col">
                                            <nav>
                                                <ul className="pagination justify-content-center">
                                                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                                        <button className="page-link" onClick={() => paginate(1)} tabIndex="-1">
                                                            <i className="ri-arrow-left-double-line"></i>
                                                        </button>
                                                    </li>
                                                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                                        <button className="page-link" onClick={() => paginate(currentPage - 1)} tabIndex="-1">
                                                            <i className="ri-arrow-left-s-line"></i>
                                                        </button>
                                                    </li>
                                                    {Array.from({ length: totalPages }, (_, index) => (
                                                        <li key={index + 1} className={`page-item ${index + 1 === currentPage ? 'active' : ''}`}>
                                                            <button className="page-link" onClick={() => paginate(index + 1)}>
                                                                {index + 1}
                                                            </button>
                                                        </li>
                                                    ))}
                                                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                                        <button className="page-link" onClick={() => paginate(currentPage + 1)}>
                                                            <i className="ri-arrow-right-s-line"></i>
                                                        </button>
                                                    </li>
                                                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                                        <button className="page-link" onClick={() => paginate(totalPages)}>
                                                            <i className="ri-arrow-right-double-fill"></i>
                                                        </button>
                                                    </li>
                                                </ul>
                                            </nav>
                                        </div>
                                        <div className="col-auto">
                                            <p className="mt-1 mb-0">Page Showing {currentPage} of {totalPages}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/*View Modal */}
            {modalData && (
                <div className="modal" tabIndex="-1" role="dialog" style={{ display: 'block', textAlign: 'center' }}>
                    <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
                        <div className="modal-content">
                            <div className="modal-header py-2">
                                <h6 className="modal-title">Travel Request Details</h6>
                                <button className="close" type="button" onClick={() => setModalData(null)}>
                                    <span aria-hidden="true"><i className="ri-close-circle-fill font-size-20"></i></span>
                                </button>
                            </div>
                            <div className="modal-body py-0">
                                <div className="row mb-2">
                                    <div className="col-3">
                                        <small className="form-text text-muted mt-2 text-right">Booking ID</small>
                                    </div>
                                    <div className="col-9">
                                        <input type="text" className="form-control-plaintext" value={modalData.travelId} readOnly />
                                    </div>
                                </div>
                                <div className="row mb-2">
                                    <div className="col-3">
                                        <small className="form-text text-muted mt-2 text-right">Employee ID</small>
                                    </div>
                                    <div className="col-9">
                                        <input type="text" className="form-control-plaintext" value={modalData.employeeId} readOnly />
                                    </div>
                                </div>
                                <div className="row mb-2">
                                    <div className="col-3">
                                        <small className="form-text text-muted mt-2 text-right">Employee Name</small>
                                    </div>
                                    <div className="col-9">
                                        <input type="text" className="form-control-plaintext" value={modalData.employeeName} readOnly />
                                    </div>
                                </div>
                                <div className="row mb-2">
                                    <div className="col-3">
                                        <small className="form-text text-muted mt-2 text-right">Journey Type</small>
                                    </div>
                                    <div className="col-9">
                                        <input type="text" className="form-control-plaintext" value={modalData.typeOfTravel} readOnly />
                                    </div>
                                </div>
                                <div className="row mb-2">
                                    <div className="col-3">
                                        <small className="form-text text-muted mt-2 text-right">TripType</small>
                                    </div>
                                    <div className="col-9">
                                        <input type="text" className="form-control-plaintext" value={modalData.tripType} readOnly />
                                    </div>
                                </div>
                                <div className="row mb-2">
                                    <div className="col-3">
                                        <small className="form-text text-muted mt-2 text-right">From</small>
                                    </div>
                                    <div className="col-9">
                                        <input type="text" className="form-control-plaintext" value={modalData.travelFrom} readOnly />
                                    </div>
                                </div>
                                {modalData.travelTo !== '' && modalData.travelTo !== null && (
                                    <div className="row mb-2">
                                        <div className="col-3">
                                            <small className="form-text text-muted mt-2 text-right">To</small>
                                        </div>
                                        <div className="col-9">
                                            <input type="text" className="form-control-plaintext" value={modalData.travelTo} readOnly />
                                        </div>
                                    </div>
                                )}
                                <div className="row mb-2">
                                    <div className="col-3">
                                        <small className="form-text text-muted mt-2 text-right">Depature Date</small>
                                    </div>
                                    <div className="col-9">
                                        <input type="text" className="form-control-plaintext" value={moment(modalData.travelStartDate).format("DD-MM-YYYY")} readOnly />
                                    </div>
                                </div>
                                {modalData.travelEndDate !== '' && modalData.travelEndDate !== null && (
                                    <div className="row mb-2">
                                        <div className="col-3">
                                            <small className="form-text text-muted mt-2 text-right">Return Date</small>
                                        </div>
                                        <div className="col-9">
                                            <input type="text" className="form-control-plaintext" value={moment(modalData.travelEndDate).format("DD-MM-YYYY")} readOnly />
                                        </div>
                                    </div>
                                )}
                                {modalData.description !== '' && modalData.description !== null && (
                                            <div className="row mb-2">
                                                <div className="col-3">
                                                    <small className="form-text text-muted mt-2 text-right">Description</small>
                                                </div>
                                                <div className="col-9">
                                                    <input type="text" className="form-control-plaintext" value={modalData.description} readOnly />
                                                </div>
                                            </div>
                                            )}
                                <div className="row mb-2">
                                    <div className="col-3">
                                        <small className="form-text text-muted mt-0 text-right">Current Status</small>
                                    </div>
                                    <div className="col-1">
                                        <span>
                                            {modalData.status.trim().toLowerCase() === 'approved' ? (
                                                <span className="badge border border-warning text-warning status">
                                                    <i className="ri-hourglass-fill"></i> {'Booking in Progress'}
                                                </span>
                                            ) : modalData.status.trim().toLowerCase() === 'rejected' ? (
                                                <span className="badge border border-danger text-danger status">
                                                    <i className="ri-thumb-down-line"></i> {modalData.status.trim()}
                                                </span>
                                            ) : modalData.status.trim().toLowerCase() === 'booked' ? (
                                                <span className="badge border border-success text-success status">
                                                    <i className="ri-verified-badge-fill"></i> {modalData.status}
                                                </span>
                                            ) : (
                                                <span className="badge border border-secondary text-secondary status">
                                                    <i className="ri-thumb-up-fill"></i> {modalData.status}
                                                </span>
                                            )}
                                        </span>
                                    </div>
                                </div>
                                {modalData.comments !== '' && modalData.comments !== null && (
                                    <div className="row mb-2">
                                        <div className="col-3">
                                            <small className="form-text text-muted mt-2 text-right">Comments</small>
                                        </div>
                                        <div className="col-9">
                                            <textarea className="form-control-plaintext" readOnly>{modalData.comments}</textarea>
                                        </div>
                                    </div>
                                )}
                                {modalData.approvedBy !== '' && modalData.approvedBy !== null && (
                                    <div className="row mb-2">
                                        <div className="col-3">
                                            <small className="form-text text-muted mt-2 text-right">Approval</small>
                                        </div>
                                        <div className="col-9">
                                            <input type="text" className="form-control-plaintext" value={modalData.approvedBy} readOnly />
                                        </div>
                                    </div>
                                )}
                                {modalData.assignTo !== '' && modalData.assignTo !== null && (
                                    <div className="row mb-2">
                                        <div className="col-3">
                                            <small className="form-text text-muted mt-2 text-right">Travel Desk Assosiate</small>
                                        </div>
                                        <div className="col-9">
                                            <input type="text" className="form-control-plaintext" value={modalData.assignTo} readOnly />
                                        </div>
                                    </div>
                                )}
                                {modalData.travelAgency !== '' && modalData.travelAgency !== null && (
                                    <div className="row mb-2">
                                        <div className="col-3">
                                            <small className="form-text text-muted mt-2 text-right">Agency</small>
                                        </div>
                                        <div className="col-9">
                                            <input type="text" className="form-control-plaintext" value={modalData.travelAgency} readOnly />
                                        </div>
                                    </div>
                                )}
                                <div className="row mb-2">
                                    <div className="col-3">
                                        <small className="form-text text-muted mt-2 text-right">Documents</small>
                                    </div>
                                    <div className="col-9">
                                        <div className="row drop-down-scroll">
                                            {modalData.travelDocuments.map((url, index) => (
                                                <div key={index}>
                                                    <a href={url} target="_blank" rel="noopener noreferrer">
                                                        {url.split('?')[0].split('/').pop().slice(0, 30)}
                                                    </a>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {/*Update Modal */}
            {updateModalData && (
                <div className="modal" tabIndex="-1" role="dialog" style={{ display: 'block', textAlign: 'center' }}>
                    <div className="modal-dialog modal-xl modal-dialog-centered" role="document">
                        <div className="modal-content">
                            <div className="modal-header py-2">
                                <h6 className="modal-title" id="updateModalLabel">Record -{updateModalData.travelId}</h6>
                                <button className="close" type="button" onClick={() => closeModal()} aria-label="Close">
                                    <span aria-hidden="true"><i className="ri-close-circle-fill font-size-20"></i></span>
                                </button>
                            </div>
                            <div className="modal-body">
                                <div className="row">
                                    <div className="col-8 border-right">
                                        <div className="row mb-2">
                                            <div className="col-3">
                                                <small className="form-text text-muted mt-2 text-right">Booking ID</small>
                                            </div>
                                            <div className="col-9">
                                                <input type="text" className="form-control-plaintext" value={updateModalData.travelId} readOnly />
                                            </div>
                                        </div>
                                        <div className="row mb-2">
                                            <div className="col-3">
                                                <small className="form-text text-muted mt-2 text-right">Employee ID</small>
                                            </div>
                                            <div className="col-9">
                                                <input type="text" className="form-control-plaintext" value={updateModalData.employeeId} readOnly />
                                            </div>
                                        </div>
                                        <div className="row mb-2">
                                            <div className="col-3">
                                                <small className="form-text text-muted mt-2 text-right">Employee Name</small>
                                            </div>
                                            <div className="col-9">
                                                <input type="text" className="form-control-plaintext" value={updateModalData.employeeName} readOnly />
                                            </div>
                                        </div>
                                        <div className="row mb-2">
                                            <div className="col-3">
                                                <small className="form-text text-muted mt-2 text-right">Journey Type</small>
                                            </div>
                                            <div className="col-9">
                                                <input type="text" className="form-control-plaintext" value={updateModalData.typeOfTravel} readOnly />
                                            </div>
                                        </div>
                                        {updateModalData.description !== '' && updateModalData.description !== null && (
                                            <div className="row mb-2">
                                                <div className="col-3">
                                                    <small className="form-text text-muted mt-2 text-right">Description</small>
                                                </div>
                                                <div className="col-9">
                                                    <input type="text" className="form-control-plaintext" value={updateModalData.description} readOnly />
                                                </div>
                                            </div>
                                            )}
                                        <div className="row mb-2">
                                            <div className="col-3">
                                                <small className="form-text text-muted mt-2 text-right">TripType</small>
                                            </div>
                                            <div className="col-9">
                                                <input type="text" className="form-control-plaintext" value={updateModalData.tripType} readOnly />
                                            </div>
                                        </div>
                                        <div className="row mb-2">
                                            <div className="col-3">
                                                <small className="form-text text-muted mt-2 text-right">From</small>
                                            </div>
                                            <div className="col-9">
                                                <input type="text" className="form-control-plaintext" value={updateModalData.travelFrom} readOnly />
                                            </div>
                                        </div>
                                        {updateModalData.travelTo !== '' && updateModalData.travelTo !== null && (
                                            <div className="row mb-2">
                                                <div className="col-3">
                                                    <small className="form-text text-muted mt-2 text-right">To</small>
                                                </div>
                                                <div className="col-9">
                                                    <input type="text" className="form-control-plaintext" value={updateModalData.travelTo} readOnly />
                                                </div>
                                            </div>
                                        )}
                                        <div className="row mb-2">
                                            <div className="col-3">
                                                <small className="form-text text-muted mt-2 text-right">Depature Date</small>
                                            </div>
                                            <div className="col-9">
                                                <input type="text" className="form-control-plaintext" value={moment(updateModalData.travelStartDate).format("DD-MM-YYYY")} readOnly />
                                            </div>
                                        </div>
                                        {updateModalData.travelEndDate !== '' && updateModalData.travelEndDate !== null && (
                                            <div className="row mb-2">
                                                <div className="col-3">
                                                    <small className="form-text text-muted mt-2 text-right">Return Date</small>
                                                </div>
                                                <div className="col-9">
                                                    <input type="text" className="form-control-plaintext" value={moment(updateModalData.travelEndDate).format("DD-MM-YYYY")} readOnly />
                                                </div>
                                            </div>
                                        )}
                                        <div className="row mb-2">
                                            <div className="col-3">
                                                <small className="form-text text-muted mt-2 text-right">Approval</small>
                                            </div>
                                            <div className="col-9">
                                                <input type="text" className="form-control-plaintext" value={updateModalData.approvedBy} readOnly />
                                            </div>
                                        </div>
                                        {updateModalData.assignTo !== '' && updateModalData.assignTo !== null && (
                                            <div className="row mb-2">
                                                <div className="col-3">
                                                    <small className="form-text text-muted mt-2 text-right">Travel Desk Assosiate</small>
                                                </div>
                                                <div className="col-9">
                                                    <input type="text" className="form-control-plaintext" value={updateModalData.assignTo} readOnly />
                                                </div>
                                            </div>
                                        )}
                                        {updateModalData.travelAgency !== '' && updateModalData.travelAgency !== null && (
                                            <div className="row mb-2">
                                                <div className="col-3">
                                                    <small className="form-text text-muted mt-2 text-right">Travel Agency</small>
                                                </div>
                                                <div className="col-9">
                                                    <input type="text" className="form-control-plaintext" value={updateModalData.travelAgency} readOnly />
                                                </div>
                                            </div>
                                        )}
                                        <div className="row mb-2">
                                            <div className="col-3">
                                                <small className="form-text text-muted mt-0 text-right">Current Status</small>
                                            </div>
                                            <div className="col-1">
                                                <span>
                                                    {updateModalData.status.trim().toLowerCase() === 'approved' ? (
                                                        <span className="badge border border-warning text-warning status">
                                                            <i className="ri-hourglass-fill"></i> {'Booking in Progress'}
                                                        </span>
                                                    ) : updateModalData.status.trim().toLowerCase() === 'rejected' ? (
                                                        <span className="badge border border-danger text-danger status">
                                                            <i className="ri-thumb-down-line"></i> {updateModalData.status.trim()}
                                                        </span>
                                                    ) : updateModalData.status.trim().toLowerCase() === 'booked' ? (
                                                        <span className="badge border border-success text-success status">
                                                            <i className="ri-verified-badge-fill"></i> {updateModalData.status}
                                                        </span>
                                                    ) : (
                                                        <span className="badge border border-secondary text-secondary status">
                                                            <i className="ri-thumb-up-fill"></i> {updateModalData.status}
                                                        </span>
                                                    )}
                                                </span>
                                            </div>
                                        </div>
                                        {updateModalData.comments !== '' && updateModalData.comments !== null && (
                                            <div className="row mb-2">
                                                <div className="col-3">
                                                    <small className="form-text text-muted mt-2 text-right">Comments</small>
                                                </div>
                                                <div className="col-6">
                                                    <textarea className="form-control-plaintext" readOnly>{updateModalData.comments}</textarea>
                                                </div>
                                            </div>
                                        )}
                                        <div className="row mb-2">
                                            <div className="col-3">
                                                <small className="form-text text-muted mt-2 text-right">Documents</small>
                                            </div>
                                            <div className="col-3">
                                                <div className="row drop-down-scroll">
                                                    {updateModalData.travelDocuments.map((url, index) => (
                                                        <div key={index}>
                                                            <a href={url} target="_blank" rel="noopener noreferrer">
                                                                {url.split('?')[0].split('/').pop().slice(0, 30)}
                                                            </a>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-4">
                                        <div className="container-fluid scroll-form">
                                            <h6>Action Required</h6>
                                            <hr />
                                            <div className="row">
                                                {updateModalData.travelAgency === null || updateModalData.travelAgency === "" && (
                                                <div className="col-lg-12 col-md-12">
                                                    <div className="form-group">
                                                        <label htmlFor="requesttravelAgency" className="required">Travel Agency</label>
                                                        {(() => {
                                                            let bookingPlaceholder = "select..";
                                                            if (updateModalData.travelAgency != null) {
                                                                bookingPlaceholder = updateModalData.travelAgency;
                                                            }
                                                            else {
                                                                bookingPlaceholder = "Select agency...";
                                                            }
                                                            return (
                                                                <Select
                                                                    className="react-select-container"
                                                                    classNamePrefix="react-select"
                                                                    name="TravelAgency"
                                                                    value={assignToOptions?.find(option => option.value === updateData.travelAgency) || null}
                                                                    onChange={(selectedOption) =>
                                                                        setUpdateData({
                                                                            ...updateData,
                                                                            travelAgency: selectedOption ? selectedOption.value : '',
                                                                        })
                                                                    }
                                                                    options={assignToOptions}
                                                                    placeholder={bookingPlaceholder}
                                                                    isClearable
                                                                    components={{ ClearIndicator }} // if you want to customize components
                                                                    menuPortalTarget={document.body} // for positioning the menu
                                                                    styles={{
                                                                        menuPortal: base => ({ ...base, zIndex: 9999 }),
                                                                        control: base => ({ ...base, zIndex: 1 })
                                                                    }}
                                                                />
                                                            );
                                                        })()}
                                                    </div>
                                                </div>
                                                )}
                                                {updateModalData.travelAgency === null || updateModalData.travelAgency === "" && (
                                                    <div className="modal-footer py-2 justify-content-center" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                                                        <button type="button" className="btn btn-primary"  onClick={async () => {
                                                            await handleSubmit();
                                                            if (typeof closeModal === 'function') closeModal();
                                                            if (typeof fetchTravelPartnerDetails === 'function') fetchTravelPartnerDetails();
                                                        }}>
                                                            Connect <i className="ri-checkbox-circle-line"></i>
                                                        </button>
                                                    </div>
                                                )}
                                                {updateModalData.travelAgency !== null && updateModalData.travelAgency !== "" && (
                                                <div className="col-lg-12 col-md-12">
                                                    <div className="form-group">
                                                        <label htmlFor="Status" className="required">
                                                            Status
                                                        </label>
                                                        {(() => {
                                                            let bookingPlaceholder = "Select status...";
                                                            if (updateModalData.status === "Approved") {
                                                                bookingPlaceholder = "Booking in Progress";
                                                            }
                                                            return (
                                                                <Select
                                                                    className="react-select-container"
                                                                    classNamePrefix="react-select"
                                                                    name="Status"
                                                                    value={
                                                                        statusOptions.find(
                                                                            (option) => option.value === updateData.status
                                                                        ) || null
                                                                    }
                                                                    onChange={(selectedOption) => {
                                                                        setUpdateData({
                                                                            ...updateData,
                                                                            status: selectedOption
                                                                                ? selectedOption.value
                                                                                : "",
                                                                        });
                                                                    }}
                                                                    options={statusOptions}
                                                                    placeholder={bookingPlaceholder}
                                                                    components={{ ClearIndicator }}
                                                                    isClearable
                                                                    menuPortalTarget={document.body}
                                                                    styles={{
                                                                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                                                        control: (base) => ({ ...base, zIndex: 1 }),
                                                                    }}
                                                                />
                                                            );
                                                        })()}
                                                    </div>
                                                </div>
                                                )}
                                                {updateModalData.travelAgency !== null && updateModalData.travelAgency !== "" && (
                                                <div className="col-lg-12 col-md-12">
                                                    <div className="form-group">
                                                        <label htmlFor="Document">
                                                            Document
                                                        </label>
                                                        <div className="d-flex align-items-center">
                                                            <div className="custom-file mr-2">
                                                                <input
                                                                    type="file"
                                                                    className="custom-file-input"
                                                                    id="Document"
                                                                    placeholder='Choose File'
                                                                    onChange={handleFileChange}
                                                                />
                                                                <label className="custom-file-label" htmlFor="uploadEmpofferletter">
                                                                    {file ? file.name : null}
                                                                </label>
                                                            </div>
                                                            <button className="btn btn-secondary" onClick={handleUpload}>
                                                                Upload
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                                )}
                                                <div className="col-lg-12 col-md-12">
                                                    <div className="container-fluid my-2 px-0">
                                                        <div className="row">
                                                            {uploadedFiles?.map((file, index) => (
                                                                <div key={index} className="col-6 mb-2">
                                                                    <button
                                                                        className="btn btn-link text-danger btn-action"
                                                                        type="button"
                                                                        onClick={() => handleDelete(index)}
                                                                    >
                                                                        <i className="ri-delete-bin-6-line" />
                                                                    </button>
                                                                    <a href={URL.createObjectURL(file)} target="_blank" rel="noopener noreferrer">
                                                                        {file.name}
                                                                    </a>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                                {updateModalData.travelAgency !== null && updateModalData.travelAgency !== "" && (
                                                <div className="form-group col-12">
                                                    <label htmlFor="notifyComments">Comments</label>
                                                    <textarea className="form-control" rows="3" value={updateData.Comments} onChange={handleUpdateChange('Comments')}></textarea>
                                                </div>
                                                )}
                                            </div>
                                        </div>
                                        {updateModalData.travelAgency !== null && updateModalData.travelAgency !== "" && (
                                            <div className="modal-footer py-2 justify-content-center">
                                                <button type="button" className="btn btn-primary" onClick={handleSubmit}>
                                                    Submit <i className="ri-checkbox-circle-line"></i>
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RequestStatus;